import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Card, Col, Row, Tooltip } from 'antd';
import { createTooltipFormatter, initEcharts, EChartsOption, applyChartTheme } from 'lib/echarts';
import dayjs from 'lib/dayjs';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  numDisplay,
  RMBSymbol,
  getAmountByCurrency,
  currencyDisplayWithUnit,
} from 'views/Dashboard/ImplementData/utils';
import { RegionFixedExchangeRateValue } from 'services/config/region';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import Empty from 'components/Empty';
import { useSize } from 'ahooks';
import { TFnType, useTranslate } from '@tolgee/react';

const Container = styled(Card)`
  margin-top: 16px;
  border-radius: 4px;
  min-height: 420px;
  background: var(--fett-bg-panel);
  border-color: var(--fett-border-default);
  .ant-card-head {
    border-bottom-color: var(--fett-border-default);
  }
  .ant-card-head-title {
    color: var(--fett-text-primary);
  }
  width: 100%;
  .ant-card-head {
    .ant-card-head-title {
      .before {
        height: 14px;
        border-left: 4px solid #3370ff;
        display: inline-block;
        margin-right: 4px;
      }
      .anticon {
        margin-left: 4px;
      }
    }
  }
  .ant-card-body {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding: 12px 24px;
    .ant-row {
      height: 50px;
      margin-bottom: 12px;
      background-color: var(--fett-bg-subtle);
      border-radius: 4px;
      align-items: center;
      padding: 0 16px;
      .title {
        color: var(--fett-text-tertiary);
      }
      .num {
        color: var(--fett-text-secondary);
        font-size: 16px;
      }
    }
  }
`;
const Left = styled.div`
  flex: 1 1 320px;
  min-width: 0;
  .pie {
    width: 100%;
    height: 210px;
  }
`;
const Relative = styled.div`
  position: relative;
  min-width: 0;
  flex: 1 1 320px;
  > .bar {
    min-height: 320px;
  }
  width: 100%;
  .ant-empty {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  .bar {
    width: 100%;
    height: 100%;
  }
`;
interface Props {
  value?: Dashboard.OrderOverviewInner;
}
const barOption = (
  value: Dashboard.OrderOverviewInner,
  t: TFnType,
  currency?: RegionFixedExchangeRateValue,
): EChartsOption => {
  const data = value['COD/非COD每日付款'] || [];
  const dateRange = data.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));
  const dataZoom = [
    {
      show: true,
      realtime: true,
      start: 0,
      end: 100,
    },
    {
      type: 'inside',
      realtime: true,
      start: 0,
      end: 100,
    },
  ];
  return {
    dataZoom: dateRange.length > 10 ? dataZoom : undefined,
    legend: {
      data: [
        t('ViewDashboard.COD付款单量'),
        t('ViewDashboard.非COD付款单量'),
        t('ViewDashboard.COD付款总金额'),
        t('ViewDashboard.非COD付款总金额'),
      ],
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: dateRange,
    },
    yAxis: [
      {
        type: 'value',
        name: '',
      },
      {
        type: 'value',
        name: `(${currency?.symbol ?? RMBSymbol})`,
        nameTextStyle: {
          align: 'left',
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
    series: [
      {
        data: data.map((x) => x.COD付款单量),
        type: 'bar',
        name: t('ViewDashboard.COD付款单量'),
        itemStyle: {
          color: '#5AD8A6',
          opacity: 0.85,
        },
        tooltip: {
          valueFormatter: createTooltipFormatter('0,0'),
        },
      },
      {
        data: data.map((x) => x.非COD付款单量),
        type: 'bar',
        name: t('ViewDashboard.非COD付款单量'),
        itemStyle: {
          color: '#5B8FF9',
          opacity: 0.85,
        },
        tooltip: {
          valueFormatter: createTooltipFormatter('0,0'),
        },
      },
      {
        data: data.map((x) => getAmountByCurrency(currency?.shorthand, x.COD付款总金额)),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        tooltip: {
          valueFormatter: createTooltipFormatter('0,0.00', currency?.symbol || RMBSymbol),
        },
        name: t('ViewDashboard.COD付款总金额'),
        itemStyle: {
          color: '#f6903d',
        },
        lineStyle: {
          color: '#f6903d',
        },
      },
      {
        data: data.map((x) => getAmountByCurrency(currency?.shorthand, x.非COD付款总金额)),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        tooltip: {
          valueFormatter: createTooltipFormatter('0,0.00', currency?.symbol || RMBSymbol),
        },
        name: t('ViewDashboard.非COD付款总金额'),
        itemStyle: {
          color: '#7262fd',
        },
        lineStyle: {
          color: '#7262fd',
        },
      },
    ],
  };
};
const pieOption = (v: Dashboard.OrderOverviewInner, t: TFnType): EChartsOption => {
  return {
    backgroundColor: '#f5f9fe',

    series: [
      {
        type: 'pie',
        radius: '50%',
        label: {
          formatter: '{b}\n{c} ({d}%)',
        },
        data: [
          {
            value: v.COD付款单量,
            name: t('ViewDashboard.COD付款单量'),
            itemStyle: {
              color: '#5AD8A6',
              opacity: 0.85,
            },
            label: {
              overflow: 'break',
            },
          },
          {
            value: v.非COD付款单量,
            name: t('ViewDashboard.非COD付款单量'),
            itemStyle: {
              color: '#5B8FF9',
              opacity: 0.85,
            },
            label: {
              overflow: 'break',
            },
          },
        ],
      },
    ],
  };
};

interface Props {
  value?: Dashboard.OrderOverviewInner;
}
const Payment = (props: Props) => {
  const { value } = props;
  const { t } = useTranslate();
  const { currency } = useDashboardCurrency();
  const chart1DomRef = useRef<HTMLDivElement>(null);
  const chart2DomRef = useRef<HTMLDivElement>(null);
  const chart1Ref = useRef<echarts.ECharts>();
  const chart2Ref = useRef<echarts.ECharts>();

  const { width: width1 } = useSize(chart1DomRef) || {};
  const { width: width2 } = useSize(chart2DomRef) || {};

  useEffect(() => {
    chart1Ref.current?.resize();
  }, [width1]);
  useEffect(() => {
    chart2Ref.current?.resize();
  }, [width2]);
  useEffect(() => {
    if (!value || !chart1DomRef.current) {
      return;
    }
    if (!chart1Ref.current) {
      chart1Ref.current = initEcharts(chart1DomRef.current);
    }
    const options = pieOption(value, t);
    chart1Ref.current.setOption(applyChartTheme(options));
  }, [value, t]);

  useEffect(() => {
    if (!value || !chart2DomRef.current) {
      return;
    }
    if (!chart2Ref.current) {
      chart2Ref.current = initEcharts(chart2DomRef.current);
    }
    const options = barOption(value, t, currency);
    chart2Ref.current.setOption(applyChartTheme(options));
  }, [value, t, currency]);
  return (
    <Container
      title={
        <div>
          <span className="before" />

          {t('ViewDashboard.COD非COD付款情况')}
          <Tooltip title={t('ViewDashboard.COD非COD付款tooltip')}>
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
      }
    >
      <Left>
        <Row>
          <Col span={10} className="title">
            {t('ViewDashboard.付款订单总量')}
          </Col>
          <Col span={14} className="num">
            {numDisplay(value?.COD非COD付款订单总量)}
          </Col>
        </Row>
        <Row>
          <Col span={10} className="title">
            {t('ViewDashboard.付款总金额')}
          </Col>
          <Col span={14} className="num">
            {currencyDisplayWithUnit(currency?.shorthand, value?.COD非COD付款总金额)}
          </Col>
        </Row>
        <Relative className="pie">
          {!value?.非COD付款单量 && !value?.COD付款单量 ? <Empty /> : null}
          <div
            className="pie"
            ref={chart1DomRef}
            style={{ display: !value?.非COD付款单量 && !value?.COD付款单量 ? 'none' : 'block' }}
          />
        </Relative>
      </Left>
      <Relative>
        <div className="bar" ref={chart2DomRef} />
        {!value?.['COD/非COD每日付款']?.length ? <Empty /> : null}
      </Relative>
    </Container>
  );
};

export default Payment;
